import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useApp } from '../context/AppContext';
import CreateProjectModal from '../components/Sidebar/BrowseTab/CreateProjectModal';
import { applyImportNext, landCreatedBook } from '../components/DocumentImport/importNext';
import AtlasIcon from '../components/icons/AtlasIcon';
import ConfirmModal from '../components/Common/ConfirmModal';
import { useConfirm } from '../components/Common/useConfirm';
import { projectsApi } from '../api/projects';
import { storiesApi } from '../api/stories';
import { resolveSeriesLandingPath, seriesDiscoveryPath } from '../utils/seriesLanding';
import './ProjectsPage.css';

/**
 * ProjectsPage — the all-projects landing page (entry point; sign-in lands here).
 *
 * "Atlas, jet-age cut" rebuild (docs/design/brainstormy-visual-identity.md §5.3):
 * a starburst header with a gilt double-rule, an auto-fit wrapping grid (the old
 * single-column collapse was the bug), and catalog-style cards — gilt frame +
 * type eyebrow + NO. 0X + Jost name + hairline + meta. The per-project accent
 * stripe is dropped; the gilt frame and eyebrow carry identity. `accent_color`
 * tints one small element per §5.3's "may optionally tint a small element"
 * clause — a 9px dot beside the name, matching LeftNav's switcher dot exactly.
 *
 * Data bindings (no backend change):
 * - Type badge + tag bind to `is_series`: stack-of-books / "Series" vs single
 *   book / "Novel" (note #4, §3/§5.3). Series is gold, novel a cooler gilt; in
 *   e-ink the badge shape + tag border carry the distinction.
 * - Catalog `NO. 0X` is the card's position in the (sorted) list — a presentation
 *   sequence, not a stored field.
 * - Meta: series shows `story_count` + last activity; a novel is a single book,
 *   so it shows last activity only (no count).
 * - `accent_color` (nullable) tints the name-row dot; falls back to
 *   `var(--gold-muted)` when unset, same fallback the switcher uses.
 *
 * Three modes: card meaning is role-token driven; gilt neutralizes to black and
 * the radius drops to 0 in high-contrast (see ProjectsPage.css).
 */

// Above this many projects the header promotes to an overflow toolbar (search +
// sort + New button + count) and the in-grid "New" card is dropped, per §5.3.
const OVERFLOW_THRESHOLD = 8;

// A create that already resolved must show its card even when the list
// request has not come back yet. Kept here so a remount still has the row.
const CREATED_PROJECTS_KEY = 'brainstormy.projects.created';

function readCreatedProjects() {
  if (typeof sessionStorage === 'undefined') return [];
  try {
    const raw = sessionStorage.getItem(CREATED_PROJECTS_KEY);
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed.filter((p) => p && p.id) : [];
  } catch {
    return [];
  }
}

function writeCreatedProjects(list) {
  if (typeof sessionStorage === 'undefined') return;
  try {
    if (!list.length) sessionStorage.removeItem(CREATED_PROJECTS_KEY);
    else sessionStorage.setItem(CREATED_PROJECTS_KEY, JSON.stringify(list));
  } catch {
    // The in-memory list still shows the card when storage is unavailable.
  }
}

function createdProjectRow(project) {
  if (!project?.id) return null;
  return {
    id: project.id,
    name: project.name || 'Untitled',
    is_series: Boolean(project.is_series),
    story_id: project.story_id || null,
    discovery_session_id: project.discovery_session_id || project.first_session_id || null,
    last_activity: project.last_activity || new Date().toISOString(),
    story_count: project.story_count || (project.story_id ? 1 : 0),
    accent_color: project.accent_color || null,
  };
}

function relativeTime(iso) {
  if (!iso) return 'No activity yet';
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return 'No activity yet';
  const secs = Math.max(0, Math.floor((Date.now() - then) / 1000));
  const units = [
    ['year', 31536000], ['month', 2592000], ['week', 604800],
    ['day', 86400], ['hour', 3600], ['minute', 60],
  ];
  for (const [name, size] of units) {
    const n = Math.floor(secs / size);
    if (n >= 1) return `${n} ${name}${n > 1 ? 's' : ''} ago`;
  }
  return 'Just now';
}

function catalogNumber(index) {
  return `NO. ${String(index + 1).padStart(2, '0')}`;
}

// Delete confirmation copy — shared by the single-project delete affordance
// and the multi-select "Delete N projects" action. Named consistently with
// sessionDeleteMessage's convention (utils/sessionDeleteCopy.js).
function projectDeleteConfig(targets) {
  const isMany = targets.length > 1;
  const names = targets.map((p) => `"${p.name}"`).join(', ');
  return {
    title: isMany ? `Delete ${targets.length} projects?` : 'Delete project?',
    message: isMany
      ? `This permanently deletes ${targets.length} projects — ${names} — and all of their stories, sessions, and documents. This cannot be undone.`
      : `This permanently deletes ${names} and all of its stories, sessions, and documents. This cannot be undone.`,
    confirmLabel: isMany ? `Delete ${targets.length} projects` : 'Delete',
  };
}

function ProjectsPage() {
  const navigate = useNavigate();
  const {
    projects, projectsError, projectsLoaded, refreshProjects, currentProject,
    setCurrentProject, setCurrentStory, setProjectAndStory, clearCurrentSession,
    leaveProject,
  } = useApp();
  const [loading, setLoading] = useState(projects.length === 0);
  const [createdProjects, setCreatedProjects] = useState(readCreatedProjects);
  // Undefined means an older caller did not report the flag. A real
  // session starts false and turns true only after the list request ends.
  const listSettled = projectsLoaded !== false;
  const waitingForProjects = loading || !listSettled;
  const [showCreate, setShowCreate] = useState(false);
  const [query, setQuery] = useState('');
  const [sortBy, setSortBy] = useState('recent'); // 'recent' | 'name'
  const [selectionMode, setSelectionMode] = useState(false);
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [isDeleting, setIsDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState(null);
  const [restoring, setRestoring] = useState(false);
  const [restoreError, setRestoreError] = useState(null);
  const [moreOpen, setMoreOpen] = useState(false);
  const restoreInputRef = useRef(null);
  const { confirm, modalProps } = useConfirm();

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        await refreshProjects();
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Retry re-triggers the actual fetch (refreshProjects -> AppContext's
  // loadProjects), not just a flag clear — a fetch that's still broken should
  // still show the error state afterward, not silently fall through to empty.
  const handleRetry = async () => {
    setLoading(true);
    try {
      await refreshProjects();
    } finally {
      setLoading(false);
    }
  };

  const catalog = useMemo(() => {
    const ids = new Set(projects.map((p) => p.id));
    const extras = createdProjects.filter((p) => p?.id && !ids.has(p.id));
    return extras.length ? [...extras, ...projects] : projects;
  }, [projects, createdProjects]);

  useEffect(() => {
    const ids = new Set(projects.map((p) => p.id));
    setCreatedProjects((prev) => {
      const next = prev.filter((p) => p?.id && !ids.has(p.id));
      if (next.length === prev.length) return prev;
      writeCreatedProjects(next);
      return next;
    });
  }, [projects]);

  const isOverflow = catalog.length > OVERFLOW_THRESHOLD;

  const visibleProjects = useMemo(() => {
    const q = query.trim().toLowerCase();
    let list = catalog;
    if (q) {
      list = list.filter((p) => (p.name || '').toLowerCase().includes(q));
    }
    const sorted = [...list];
    if (sortBy === 'name') {
      sorted.sort((a, b) => (a.name || '').localeCompare(b.name || ''));
    } else {
      // Recent: last_activity desc, nulls last.
      sorted.sort((a, b) => {
        const ta = a.last_activity ? new Date(a.last_activity).getTime() : 0;
        const tb = b.last_activity ? new Date(b.last_activity).getTime() : 0;
        return tb - ta;
      });
    }
    return sorted;
  }, [catalog, query, sortBy]);

  const openProject = async (project, navState) => {
    const go = (path, replace) => {
      if (!navState) {
        navigate(path);
        return;
      }
      navigate(path, replace ? { state: navState, replace: true } : { state: navState });
    };
    // Series landing (#682): empty series, no Timeline pins → Series
    // Discovery (named that, not leftover book Discovery). Series that
    // already has pins → Timeline. Never leftover Start Book 1, never
    // leftover Series Notes /dashboard, never a book selected so Talk /
    // Snapshots / Outline for a book do not greet first open.
    if (project.is_series) {
      setCurrentProject(project);
      setCurrentStory?.(null);
      // Enter on first click. Waiting on the board read before navigate
      // only updates the breadcrumb — second-click-to-enter is FAIL (#915).
      const immediate = seriesDiscoveryPath(project);
      go(immediate);
      const path = await resolveSeriesLandingPath(project);
      if (path !== immediate) go(path, true);
      return;
    }
    // Single-book (#664): land in the existing Discovery notebook — the
    // sitting they already have. Newest session is the wrong door: list
    // order is created_at DESC, so a fresh empty Explore/Focus wins.
    // New sitting only via Create New Session. Do not land in Snapshots
    // or Outline. Bare /chat still can't be trusted (global projects[0]).
    try {
      const stories = await storiesApi.list(project.id);
      const story = stories?.[0] ?? null;
      if (story) {
        setProjectAndStory(project, story);
        if (story.discovery_session_id) {
          go(`/chat/${story.discovery_session_id}`);
          return;
        }
        const ensured = await storiesApi.ensureDiscoverySession(story.id);
        const sessionId = ensured?.session_id || ensured?.id;
        if (sessionId) {
          go(`/chat/${sessionId}`);
          return;
        }
      }
    } catch {
      // fall through to project Discovery / bare chat
    }
    setCurrentProject(project);
    if (project.discovery_session_id) {
      go(`/chat/${project.discovery_session_id}`);
      return;
    }
    go('/chat');
  };

  // Upload keeps the modal open through Digest approved. The book was
  // created at Continue to upload, so the card has to land then — not
  // when the modal finally closes.
  const rememberListedProject = (newProject) => {
    const row = createdProjectRow(newProject);
    if (!row) return;
    setCreatedProjects((prev) => {
      const next = [row, ...prev.filter((p) => p.id !== row.id)];
      writeCreatedProjects(next);
      return next;
    });
    setLoading(false);
  };

  const handleCreated = (newProject, options) => {
    rememberListedProject(newProject);
    setShowCreate(false);
    if (newProject?.story_id) {
      setProjectAndStory(newProject, {
        id: newProject.story_id,
        project_id: newProject.id,
        name: newProject.name,
        discovery_session_id: newProject.discovery_session_id || null,
      });
    } else {
      setCurrentProject(newProject);
    }
    if (landCreatedBook(navigate, newProject, options)) {
      void refreshProjects();
      return;
    }
    if (options?.next) {
      applyImportNext(navigate, options, newProject, {
        currentStoryId: options.story_id || newProject?.story_id,
      });
    } else {
      const sessionId = newProject?.discovery_session_id || newProject?.first_session_id;
      navigate(sessionId ? `/chat/${sessionId}` : '/chat');
    }
    // The create already resolved. Do not wait on the list request.
    void refreshProjects();
  };

  const toggleSelectionMode = () => {
    setSelectionMode((v) => !v);
    setSelectedIds(new Set());
    setDeleteError(null);
  };

  const toggleSelected = (projectId) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(projectId)) next.delete(projectId);
      else next.add(projectId);
      return next;
    });
  };

  // Delete flow shared by the single-project card affordance and the
  // multi-select bulk action. Calls happen sequentially, not in parallel: if
  // one fails, the transaction guarantee on the backend means it was left
  // untouched, so we stop immediately rather than continue deleting the rest
  // out from under a failure the writer hasn't seen yet.
  const runDelete = async (targets) => {
    const { title, message, confirmLabel } = projectDeleteConfig(targets);
    const ok = await confirm({ title, message, confirmLabel, destructive: true });
    if (!ok) return;

    setIsDeleting(true);
    setDeleteError(null);
    let failed = null;
    const deletedIds = [];
    for (const project of targets) {
      try {
        await projectsApi.delete(project.id);
        deletedIds.push(project.id);
      } catch (err) {
        failed = { name: project.name, message: err.message || 'Something went wrong.' };
        break;
      }
    }
    if (currentProject && deletedIds.includes(currentProject.id)) {
      leaveProject();
      clearCurrentSession?.();
    }
    await refreshProjects();
    setIsDeleting(false);
    if (failed) {
      setDeleteError(failed);
    } else {
      setSelectionMode(false);
      setSelectedIds(new Set());
    }
  };

  const selectedProjects = useMemo(
    () => projects.filter((p) => selectedIds.has(p.id)),
    [projects, selectedIds],
  );

  const handleDeleteSelected = () => runDelete(selectedProjects);
  const handleDeleteSingle = (project) => runDelete([project]);

  const handleRestoreFile = async (event) => {
    const file = event.target.files && event.target.files[0];
    event.target.value = '';
    if (!file) return;
    setRestoring(true);
    setRestoreError(null);
    const form = new FormData();
    form.append('file', file);
    let result;
    try {
      result = await projectsApi.restoreBackup(form);
    } catch (err) {
      setRestoreError(
        (err && err.message) || 'This backup could not be restored. Nothing was changed.',
      );
      setRestoring(false);
      return;
    }
    const project = result && result.project;
    if (!project || !project.id) {
      setRestoreError('This backup could not be restored. Nothing was changed.');
      setRestoring(false);
      return;
    }
    try {
      await refreshProjects();
    } catch {
      // The new project already exists. Open it even if the list refresh failed.
    }
    try {
      await openProject(project, {
        restoreNotice: result.message || 'This project was restored from a backup.',
        restoreWarnings: Array.isArray(result.warnings) ? result.warnings : [],
      });
    } finally {
      setRestoring(false);
    }
  };

  const count = catalog.length;

  return (
    <main className="projects-page">
      <header className="projects-page__header">
        <div className="projects-page__headline">
          <div className="projects-page__heading">
            <h1 className="projects-page__title">PROJECTS</h1>
            <p className="projects-page__subtitle">
              Pick up where you left off, or start a new story.
            </p>
            <p className="projects-page__type-help">
              A Novel is one book; a Series is several books that share a world or people.
            </p>
          </div>
          <AtlasIcon name="starburst" size={40} className="projects-page__starburst" />
        </div>

        {!waitingForProjects && (
          <div className="projects-page__actions">
            <button
              type="button"
              className="projects-page__start"
              onClick={() => setShowCreate(true)}
            >
              Start new story
            </button>
            <div className="projects-page__more">
              <button
                type="button"
                className="projects-page__more-btn"
                aria-haspopup="menu"
                aria-expanded={moreOpen}
                aria-label="More project actions"
                onClick={() => setMoreOpen((open) => !open)}
              >
                More
              </button>
              {moreOpen && (
                <div className="projects-page__more-menu" role="menu" aria-label="More project actions">
                  <button
                    type="button"
                    role="menuitem"
                    className="projects-page__more-item"
                    onClick={() => {
                      setMoreOpen(false);
                      if (restoreInputRef.current) restoreInputRef.current.click();
                    }}
                    disabled={restoring}
                  >
                    {restoring ? 'Restoring…' : 'Restore from backup'}
                  </button>
                  {catalog.length > 0 && !selectionMode && (
                    <button
                      type="button"
                      role="menuitem"
                      className="projects-page__more-item"
                      onClick={() => {
                        setMoreOpen(false);
                        toggleSelectionMode();
                      }}
                    >
                      Select projects to delete
                    </button>
                  )}
                </div>
              )}
            </div>
            <input
              ref={restoreInputRef}
              type="file"
              accept=".zip,application/zip"
              className="projects-restore__file"
              aria-hidden="true"
              tabIndex={-1}
              onChange={handleRestoreFile}
            />
          </div>
        )}

        {isOverflow && (
          <div className="projects-toolbar" role="search">
            <div className="projects-toolbar__search">
              <AtlasIcon name="search" size={16} className="projects-toolbar__search-icon" />
              <input
                type="text"
                className="projects-toolbar__search-input"
                placeholder="Search projects"
                aria-label="Search projects"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
              />
            </div>
            <label className="projects-toolbar__sort">
              <span className="projects-toolbar__sort-label">Sort</span>
              <select
                className="projects-toolbar__sort-select"
                aria-label="Sort projects"
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
              >
                <option value="recent">Recent</option>
                <option value="name">Name</option>
              </select>
            </label>
            <span className="projects-toolbar__count">
              {count} {count === 1 ? 'project' : 'projects'}
            </span>
          </div>
        )}

        {/* Gilt double-rule (two gilt rules, the second at 45%, 2px apart). */}
        <div className="projects-page__rule" aria-hidden="true">
          <span className="projects-page__rule-line" />
          <span className="projects-page__rule-line projects-page__rule-line--faint" />
        </div>

        {/* Restore lives in More, beside Start new story. */}
      </header>

      {!waitingForProjects && !projectsError && selectionMode && (
        <div className="projects-selection-bar">
          {selectionMode ? (
            <>
              <span className="projects-selection-bar__count">
                {selectedIds.size} selected
              </span>
              <button
                type="button"
                className="projects-selection-bar__delete"
                onClick={handleDeleteSelected}
                disabled={selectedIds.size === 0 || isDeleting}
              >
                {selectedIds.size > 0
                  ? `Delete ${selectedIds.size} ${selectedIds.size === 1 ? 'project' : 'projects'}`
                  : 'Delete selected'}
              </button>
              <button
                type="button"
                className="projects-selection-bar__cancel"
                onClick={toggleSelectionMode}
                disabled={isDeleting}
              >
                Cancel
              </button>
            </>
          ) : (
            <button type="button" className="projects-selection-bar__select" onClick={toggleSelectionMode}>
              Select projects to delete
            </button>
          )}
        </div>
      )}

      {restoreError && (
        <div className="projects-page__error" role="alert">
          <p className="projects-page__error-message">{restoreError}</p>
          <button type="button" className="projects-page__error-retry" onClick={() => setRestoreError(null)}>
            Dismiss
          </button>
        </div>
      )}

      {deleteError && (
        <div className="projects-page__error" role="alert">
          <p className="projects-page__error-message">
            Couldn&rsquo;t delete &ldquo;{deleteError.name}&rdquo;: {deleteError.message} Nothing else was deleted.
          </p>
          <button type="button" className="projects-page__error-retry" onClick={() => setDeleteError(null)}>
            Dismiss
          </button>
        </div>
      )}

      {waitingForProjects ? (
        <p className="projects-page__loading">Loading your projects…</p>
      ) : projectsError ? (
        <div className="projects-page__error" role="alert">
          <p className="projects-page__error-message">{projectsError}</p>
          <button type="button" className="projects-page__error-retry" onClick={handleRetry}>
            Retry
          </button>
        </div>
      ) : (
        <ul className="projects-grid" aria-label="Your projects">
          {visibleProjects.map((p, i) => {
            const isSelected = selectedIds.has(p.id);
            const cardContent = (
              <>
                {selectionMode && (
                  <input
                    type="checkbox"
                    className="project-card__checkbox"
                    checked={isSelected}
                    onChange={() => toggleSelected(p.id)}
                    onClick={(e) => e.stopPropagation()}
                    aria-label={`Select ${p.name}`}
                  />
                )}
                <span className="project-card__row1">
                  <span className="project-card__type">
                    <AtlasIcon
                      name={p.is_series ? 'series' : 'novel'}
                      size={22}
                      className={`project-card__type-badge project-card__type-badge--${p.is_series ? 'series' : 'novel'}`}
                    />
                    <span className={`project-card__type-tag project-card__type-tag--${p.is_series ? 'series' : 'novel'}`}>
                      {p.is_series ? 'Series' : 'Novel'}
                    </span>
                  </span>
                  <span className="project-card__catalog">{catalogNumber(i)}</span>
                </span>
                <span className="project-card__name-row">
                  <span
                    className="project-card__accent-dot"
                    style={{ background: p.accent_color || 'var(--gold-muted)' }}
                    aria-hidden="true"
                  />
                  <span className="project-card__name">{p.name}</span>
                </span>
                <span className="project-card__hairline" aria-hidden="true" />
                <span className="project-card__meta">
                  {p.is_series ? (
                    <>
                      {(p.story_count || 0)} {p.story_count === 1 ? 'BOOK' : 'BOOKS'}
                      <span className="project-card__sep" aria-hidden="true">·</span>
                      {relativeTime(p.last_activity).toUpperCase()}
                    </>
                  ) : (
                    relativeTime(p.last_activity).toUpperCase()
                  )}
                </span>
              </>
            );

            return (
              <li key={p.id}>
                {selectionMode ? (
                  <div
                    className={`project-card project-card--selectable${isSelected ? ' project-card--selected' : ''}`}
                    onClick={() => toggleSelected(p.id)}
                  >
                    {cardContent}
                  </div>
                ) : (
                  // Delete is a SIBLING button, not nested inside the card
                  // button — a <button> can't validly contain another
                  // <button>, and keeping the card itself a real <button>
                  // preserves keyboard/AT semantics other tests rely on.
                  <>
                    <button type="button" className="project-card" onClick={() => openProject(p)}>
                      {cardContent}
                    </button>
                    <button
                      type="button"
                      className="project-card__delete-btn"
                      onClick={() => handleDeleteSingle(p)}
                      aria-label={`Delete project: ${p.name}`}
                      title="Delete project"
                    >
                      ×
                    </button>
                  </>
                )}
              </li>
            );
          })}

        </ul>
      )}

      {listSettled && !loading && !projectsError && catalog.length === 0 && (
        <p className="projects-page__empty">No projects yet. Start a new story to begin.</p>
      )}
      {listSettled && !loading && !projectsError && isOverflow && visibleProjects.length === 0 && (
        <p className="projects-page__empty">No projects match “{query}”.</p>
      )}

      {showCreate && (
        <CreateProjectModal
          onClose={() => setShowCreate(false)}
          onListed={rememberListedProject}
          onCreated={handleCreated}
        />
      )}

      <ConfirmModal {...modalProps} />
    </main>
  );
}

export default ProjectsPage;
